import * as React from "react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { track } from "./analytics"

/**
 * The copy half of the 404: what happened, the address that failed, and the
 * ways out. Every piece shares this, so a design choice in the art can never
 * quietly change what the page offers.
 */
export interface ExitLink {
  labelKey: string;
  label: string;
  href: string;
  isExternal?: boolean;
}

export const NotFoundCopy: React.FC<{ line?: string; variant: string; links?: ExitLink[] }> = ({
  line,
  variant,
  links,
}) => {
  const { t } = useTranslation("common")
  // Read after mount: during prerender there is no location, and rendering a
  // guess would make the server and client markup disagree.
  const [path, setPath] = useState<string | null>(null)
  useEffect(() => setPath(window.location.pathname), [])

  const exits = links || []

  return (
    <div className="nf-copy">
      <div className="text-[12px] uppercase tracking-[0.18em] text-tertiary mb-3">404</div>
      <h1 className="text-3xl md:text-[34px] font-bold tracking-tight text-primary leading-[1.1] mb-3">
        Page not found
      </h1>
      {line ? <p className="text-[15px] text-secondary leading-relaxed mb-1">{line}</p> : null}
      <p className="text-[15px] text-secondary leading-relaxed mb-6">
        {path ? (
          <code className="text-[13px] text-primary bg-secondary rounded px-1.5 py-0.5">{path}</code>
        ) : (
          <span className="text-tertiary">this address</span>
        )}{" "}
        does not exist.
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {exits.map((l) => {
          const label = t(l.labelKey, l.label)
          const onClick = () => track("hero_cta", { variant, surface: "404", label: l.href })
          const inner = (
            <span className="inline-flex items-baseline gap-1.5 group">
              <span className="border-b border-transparent group-hover:border-accent transition-colors">
                {label}
              </span>
              <span className="text-accent">→</span>
            </span>
          )
          return l.isExternal ? (
            <a key={l.href} href={l.href} onClick={onClick}
               className="text-[15px] text-primary hover:text-primary">{inner}</a>
          ) : (
            <Link key={l.href} to={l.href} onClick={onClick}
               className="text-[15px] text-primary hover:text-primary">{inner}</Link>
          )
        })}
      </div>
    </div>
  )
}

/**
 * The stage. 2:1 on desktop with the copy overlaid at the left, and on small
 * screens it unstacks: the art gets a fixed height and the copy sits under it,
 * because overlaying text on a 320px-wide simulation is unreadable.
 */
export const Stage: React.FC<{
  stageRef: React.RefObject<HTMLDivElement>
  children: React.ReactNode
  line?: string
  variant: string
  links?: ExitLink[]
}> = ({ stageRef, children, line, variant, links }) => (
  <div className="nf-stage" ref={stageRef} data-hero-zone>
    {children}
    <NotFoundCopy line={line} variant={variant} links={links} />
  </div>
)
